import React from 'react';
import {
  Box,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import dayjs from 'dayjs';
import HistoryPagination from 'components/HistoryPagination';
import { LearningSessionHistory } from 'app/types';
import { modeById } from 'pages/Learning/learningMeta';
import LearningModeIcon from 'pages/Learning/components/LearningModeIcon';

interface LearningHistoryListProps {
  history: LearningSessionHistory[];
  page: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  onOpen: (id: number) => void;
  onDelete: (id: number) => void;
}

const POSITIVE = '#3B6D11';

const formatDateTime = (iso: string) => dayjs(iso).format('DD.MM · HH:mm');

const LearningHistoryList: React.FC<LearningHistoryListProps> = ({
  history,
  page,
  totalPages,
  onChangePage,
  onOpen,
  onDelete,
}) => {
  if (!history.length) {
    return null;
  }

  const GRID_COLS_MD = '130px minmax(0, 200px) minmax(0, 1fr) 130px 56px';

  return (
    <Box>
      <Stack
        alignItems={'baseline'}
        direction={'row'}
        justifyContent={'space-between'}
        sx={{ mb: 1.5 }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
          {'Історія вправ'}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
          {`Усього ${history.length}`}
        </Typography>
      </Stack>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.tableBorder',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'primary.tableBorder',
            display: { xs: 'none', md: 'grid' },
            gap: 2,
            gridTemplateColumns: GRID_COLS_MD,
            px: 2.5,
            py: 1.25,
          }}
        >
          {['Коли', 'Режим', 'Результат', 'Статус', ''].map((t, i) => (
            <Typography
              key={i}
              sx={{
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {t}
            </Typography>
          ))}
        </Box>

        {history.map((item, index) => {
          const mode = modeById(item.mode);
          const isDone = item.status === 'DONE';
          return (
            <Box
              key={item.id}
              onClick={() => onOpen(item.id)}
              sx={{
                alignItems: 'center',
                borderTop: index > 0 ? '1px solid' : 'none',
                borderColor: 'primary.tableBorder',
                cursor: 'pointer',
                display: 'grid',
                gap: { xs: 1.25, md: 2 },
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr) auto auto',
                  md: GRID_COLS_MD,
                },
                px: { xs: 1.75, md: 2.5 },
                py: 1.5,
                transition: 'background-color 120ms ease',
                '&:hover': { bgcolor: 'primary.body' },
              }}
            >
              <Typography
                sx={{ fontSize: 14, order: { xs: 1, md: 0 } }}
              >
                {formatDateTime(item.createdAt)}
              </Typography>

              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={0.75}
                sx={{
                  minWidth: 0,
                  order: { xs: 3, md: 0 },
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                }}
              >
                {mode && (
                  <Box
                    sx={{
                      color: 'primary.main',
                      display: 'inline-flex',
                    }}
                  >
                    <LearningModeIcon name={mode.icon} size={18} />
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mode?.label ?? item.mode}
                </Typography>
              </Stack>

              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={1.5}
                sx={{
                  minWidth: 0,
                  order: { xs: 4, md: 0 },
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.purple',
                    borderRadius: 999,
                    display: { xs: 'none', sm: 'block' },
                    flex: 1,
                    height: 8,
                    maxWidth: 220,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      height: '100%',
                      opacity: 0.75,
                      width: '100%',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box
                    component={'b'}
                    sx={{ color: 'text.primary', fontWeight: 800 }}
                  >
                    {item.wordsCount}
                  </Box>
                  {' слів'}
                </Typography>
              </Stack>

              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={0.5}
                sx={{
                  color: isDone ? POSITIVE : 'primary.main',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {isDone ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PendingIcon sx={{ fontSize: 16 }} />
                )}
                <Box component={'span'}>
                  {isDone ? 'Завершено' : 'В процесі'}
                </Box>
              </Stack>

              <Stack
                direction={'row'}
                spacing={0.25}
                sx={{
                  justifySelf: 'flex-end',
                  order: { xs: 2, md: 0 },
                }}
              >
                <IconButton
                  aria-label={'Видалити сесію'}
                  size={'small'}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(item.id);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <DeleteOutlineIcon fontSize={'small'} />
                </IconButton>
                <ChevronRightIcon
                  sx={{ alignSelf: 'center', color: 'text.secondary' }}
                />
              </Stack>
            </Box>
          );
        })}
      </Box>

      <HistoryPagination
        currentPage={page}
        totalPages={totalPages}
        onChange={onChangePage}
      />
    </Box>
  );
};

export default LearningHistoryList;
